import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Alert,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import {
  AFFILIATE_PAGE_SIZE,
  createAffiliate,
  getAffiliate,
  getAffiliateUsers,
  updateAffiliate,
} from "services/affiliates";

function AffiliateManagement({ userId, isAffiliate, onAssigned }) {
  const sessionId = useSelector((state) => state.login.sessionId);
  const navigate = useNavigate();
  const active = useRef(false);
  const [name, setName] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [profile, setProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(isAffiliate);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [commission, setCommission] = useState("");
  const [savingCommission, setSavingCommission] = useState(false);
  const [commissionMessage, setCommissionMessage] = useState(null);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAffiliate) {
      setProfile(null);
      setUsers([]);
      setOffset(0);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    async function loadAffiliate() {
      setLoading(true);
      setError("");
      try {
        const [affiliate, referredUsers] = await Promise.all([
          getAffiliate(userId, sessionId, { signal: controller.signal }),
          getAffiliateUsers(userId, offset, sessionId, {
            signal: controller.signal,
          }),
        ]);
        if (
          !affiliate ||
          typeof affiliate !== "object" ||
          !Array.isArray(referredUsers)
        ) {
          throw new Error(
            "The affiliate service returned an invalid response."
          );
        }
        if (!controller.signal.aborted) {
          setProfile(affiliate);
          setCommission(String(affiliate.commissionPercent ?? ""));
          setCommissionMessage(null);
          setUsers(referredUsers);
        }
      } catch (requestError) {
        if (!controller.signal.aborted) {
          setError(requestError.message || "Unable to load affiliate details.");
          if (requestError.status === 401 || requestError.status === 403) {
            navigate("/authentication/sign-in");
          }
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadAffiliate();
    return () => controller.abort();
  }, [userId, isAffiliate, sessionId, offset, retry, navigate]);

  const assignAffiliate = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Enter an affiliate name.");
      return;
    }
    if (!Number.isSafeInteger(Number(userId)) || Number(userId) <= 0) {
      setError("A valid user ID is required to assign an affiliate.");
      return;
    }
    if (assigning) return;

    setAssigning(true);
    setError("");
    try {
      await createAffiliate(userId, name, sessionId);
      if (active.current) onAssigned();
    } catch (requestError) {
      if (active.current) {
        setError(requestError.message || "Unable to assign affiliate.");
        if (requestError.status === 401 || requestError.status === 403) {
          navigate("/authentication/sign-in");
        }
      }
    } finally {
      if (active.current) setAssigning(false);
    }
  };

  const saveCommission = async (event) => {
    event.preventDefault();
    const value = Number(commission);
    if (
      !commission.trim() ||
      !Number.isFinite(value) ||
      value < 0 ||
      value > 100
    ) {
      setCommissionMessage({
        severity: "error",
        text: "Enter a commission between 0 and 100%.",
      });
      return;
    }
    if (savingCommission) return;

    setSavingCommission(true);
    setCommissionMessage(null);
    try {
      const updated = await updateAffiliate(
        userId,
        { commissionPercent: value },
        sessionId
      );
      if (
        !updated ||
        typeof updated.commissionPercent !== "number" ||
        !Number.isFinite(updated.commissionPercent) ||
        updated.commissionPercent < 0 ||
        updated.commissionPercent > 100
      ) {
        throw new Error(
          "The affiliate service returned an invalid commission response."
        );
      }
      if (active.current) {
        setProfile(updated);
        setCommission(String(updated.commissionPercent));
        setCommissionMessage({
          severity: "success",
          text: "Affiliate commission saved.",
        });
      }
    } catch (requestError) {
      if (active.current) {
        setCommissionMessage({
          severity: "error",
          text: requestError.message || "Unable to save affiliate commission.",
        });
        if (requestError.status === 401 || requestError.status === 403) {
          navigate("/authentication/sign-in");
        }
      }
    } finally {
      if (active.current) setSavingCommission(false);
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <MDBox p={3}>
        <MDTypography variant="h6" mb={2}>
          Affiliate
        </MDTypography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!isAffiliate ? (
          <MDBox component="form" onSubmit={assignAffiliate}>
            <MDTypography variant="button" display="block" color="text" mb={2}>
              Create an affiliate profile and grant this user the affiliate
              role.
            </MDTypography>
            <MDInput
              label="Affiliate name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={assigning}
              fullWidth
            />
            <MDButton
              type="submit"
              color="info"
              disabled={assigning || !name.trim()}
              sx={{ mt: 2 }}
            >
              {assigning ? "Assigning..." : "Assign affiliate"}
            </MDButton>
          </MDBox>
        ) : loading ? (
          <CircularProgress size={28} aria-label="Loading affiliate details" />
        ) : error ? (
          <MDButton color="info" onClick={() => setRetry((value) => value + 1)}>
            Retry affiliate details
          </MDButton>
        ) : (
          profile && (
            <>
              <TableContainer>
                <Table size="small" aria-label="Affiliate profile">
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      {[
                        "Profile ID",
                        "User ID",
                        "Key",
                        "Name",
                        "Commission",
                        "Created at",
                      ].map((label) => (
                        <TableCell key={label}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{profile.id}</TableCell>
                      <TableCell>{profile.userId}</TableCell>
                      <TableCell>{profile.key}</TableCell>
                      <TableCell>{profile.name}</TableCell>
                      <TableCell>
                        {profile.commissionPercent == null
                          ? "Not set"
                          : `${profile.commissionPercent}%`}
                      </TableCell>
                      <TableCell>{profile.createdAt}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <MDBox component="form" onSubmit={saveCommission} mt={3}>
                <MDInput
                  label="Affiliate commission (%)"
                  type="number"
                  value={commission}
                  onChange={(event) => {
                    setCommission(event.target.value);
                    setCommissionMessage(null);
                  }}
                  inputProps={{ min: 0, max: 100, step: "any" }}
                  disabled={savingCommission}
                  fullWidth
                />
                <MDButton
                  type="submit"
                  color="info"
                  disabled={savingCommission}
                  sx={{ mt: 2 }}
                >
                  {savingCommission ? "Saving..." : "Save commission"}
                </MDButton>
                {commissionMessage && (
                  <Alert severity={commissionMessage.severity} sx={{ mt: 2 }}>
                    {commissionMessage.text}
                  </Alert>
                )}
              </MDBox>
              <MDTypography variant="h6" mt={3} mb={1}>
                Referred users
              </MDTypography>
              <TableContainer>
                <Table size="small" aria-label="Referred users">
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      {["User ID", "First name", "Last name", "Email"].map(
                        (label) => (
                          <TableCell key={label}>{label}</TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Link to={`/profile/${user.id}`}>{user.id}</Link>
                        </TableCell>
                        <TableCell>{user.firstName}</TableCell>
                        <TableCell>{user.lastName}</TableCell>
                        <TableCell>{user.email}</TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          {offset === 0
                            ? "No referred users yet."
                            : "No more referred users."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <MDBox display="flex" alignItems="center" gap={2} mt={2}>
                <MDButton
                  color="info"
                  disabled={savingCommission || offset === 0}
                  onClick={() =>
                    setOffset((value) => value - AFFILIATE_PAGE_SIZE)
                  }
                >
                  Previous
                </MDButton>
                <MDTypography variant="button">
                  Page {offset / AFFILIATE_PAGE_SIZE + 1}
                </MDTypography>
                <MDButton
                  color="info"
                  disabled={
                    savingCommission || users.length < AFFILIATE_PAGE_SIZE
                  }
                  onClick={() =>
                    setOffset((value) => value + AFFILIATE_PAGE_SIZE)
                  }
                >
                  Next
                </MDButton>
              </MDBox>
            </>
          )
        )}
      </MDBox>
    </Card>
  );
}

AffiliateManagement.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isAffiliate: PropTypes.bool.isRequired,
  onAssigned: PropTypes.func.isRequired,
};

export default AffiliateManagement;
